/**
 * ApiKeysCard — mint and revoke the API keys that let an external AI agent
 * (Claude Code, or any MCP client) act on the user's own Fias data.
 *
 * This is the surface `@fias/mcp-server`'s README points at ("Fias account →
 * API keys"). Until now it did not exist, so the documented setup flow was
 * impossible to complete without hand-rolling an HTTP call.
 *
 * Two scope decisions are deliberate:
 *
 *   1. ONLY the two `agent-tools:*` scopes are offered. The server's allowlist
 *      (`routes/api-keys/index.ts`) also accepts a bare arche id, but that
 *      scopes a key for the SEPARATE external plane
 *      (`/v1/external/arches/invoke`) — surfacing it next to MCP setup
 *      instructions would strongly imply an arche can be driven over MCP,
 *      which is not how the agent-tools projection works.
 *
 *   2. Write access is one checkbox, not a per-tool picker, because that is
 *      exactly the shape the server enforces: `agentExposableSpecs(allowWrites)`
 *      takes a single boolean. A finer-grained UI here would be inventing a
 *      control the backend does not have.
 *
 * Outward actions (sending email, publishing posts) are never available to an
 * external agent at any scope — that is a structural property of the headless
 * policy, not something this UI can grant, so it is stated as a fact.
 *
 * NOTE (pending): whether plugin arches will ever be reachable over MCP is an
 * open product question. Today they are not — only platform arches ship
 * Fias-AI tools. The copy below deliberately states what IS reachable rather
 * than making a claim about plugins in either direction; add the explicit
 * sentence once that decision is made.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Copy, Check, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

const READ_SCOPE = 'agent-tools:read';
const WRITE_SCOPE = 'agent-tools:write';
const WORKSPACE_SCOPE_PREFIX = 'workspace:';

/** The sentinel for "no workspace binding" — the key acts on personal data. */
const PERSONAL = '__personal__';

/** Mirrors MAX_KEYS_PER_USER in routes/api-keys/index.ts. */
const MAX_KEYS = 25;

interface ApiKeyRow {
  keyId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  status: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

/** The workspace a key is bound to, or null when it acts on personal data. */
function boundWorkspaceId(scopes: string[]): string | null {
  const scope = scopes.find((s) => s.startsWith(WORKSPACE_SCOPE_PREFIX));
  return scope ? scope.slice(WORKSPACE_SCOPE_PREFIX.length) : null;
}

function formatDate(value: string | null): string {
  if (!value) return 'never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toLocaleDateString();
}

export function ApiKeysCard() {
  // Guests must never see this card. Every route behind it is
  // `requireFullAccount` (a guest holding an API key would launder its own
  // guest-ness off the session — see routes/api-keys/index.ts), so for a guest
  // the card can only ever render a 403. Account Settings sits behind a bare
  // `ProtectedRoute`, which a guest satisfies — every sibling card there uses
  // `requireAuth` and degrades to an empty state, so without this the API Keys
  // card would be the only one showing an error.
  const { isGuest } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [allowWrites, setAllowWrites] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string>(PERSONAL);
  // The plaintext secret is returned exactly once, by the create call. It is
  // held in component state only — never refetched, never persisted — and is
  // cleared when the user dismisses it.
  const [freshSecret, setFreshSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.apiKeys.lists(),
    queryFn: () => api.listApiKeys(),
    // Disabled rather than guarded at render: a guest must not fire the
    // request at all, and every hook still runs unconditionally above the
    // early return below (rules of hooks).
    enabled: !isGuest,
  });

  // Only ORG workspaces are offered. Guild data spaces are deliberately
  // excluded: the server refuses them (HOLDER_TYPE_NOT_ACTABLE) because the
  // headless plane stays no broader than the interactive assistant, which
  // refuses non-org Holders. Offering one here would be an option that fails.
  const { data: holderData } = useQuery({
    queryKey: queryKeys.vault.me.memberships(),
    queryFn: () => api.listMyVaultMemberships(),
    enabled: !isGuest,
  });
  const workspaces = (holderData?.memberships ?? []).filter(
    (m) => m.holderType === 'org' && m.status === 'active',
  );
  const workspaceName = (id: string): string =>
    workspaces.find((w) => w.holderId === id)?.holderDisplayName ?? 'a workspace';

  const keys: ApiKeyRow[] = data?.keys ?? [];
  const activeKeys = keys.filter((k) => k.status === 'active');
  const atLimit = activeKeys.length >= MAX_KEYS;

  const createMutation = useMutation({
    mutationFn: () =>
      api.createApiKey({
        name: name.trim(),
        scopes: [
          READ_SCOPE,
          ...(allowWrites ? [WRITE_SCOPE] : []),
          ...(workspaceId === PERSONAL ? [] : [`${WORKSPACE_SCOPE_PREFIX}${workspaceId}`]),
        ],
      }),
    onSuccess: (result) => {
      setFreshSecret(result.secret);
      setName('');
      setAllowWrites(false);
      setWorkspaceId(PERSONAL);
      setError('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => api.revokeApiKey(keyId),
    onSuccess: () => {
      setError('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    },
  });

  async function handleCopy() {
    if (!freshSecret) return;
    try {
      await navigator.clipboard.writeText(freshSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked by permissions policy; the secret is visible
      // and selectable in the field regardless, so this is not worth an error.
    }
  }

  if (isGuest) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          API Keys
        </CardTitle>
        <CardDescription>
          Connect an external AI agent — Claude Code, or any client that speaks the Model Context
          Protocol — to your own Fias data: email, calendar, todo lists, contacts, documents, and
          more. Agents can never send email, publish posts, or take any other outward action; those
          always require your approval here in the app.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        {/* One-time secret reveal */}
        {freshSecret && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2">
            <p className="text-sm font-medium">Copy this key now — it will not be shown again.</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={freshSecret} aria-label="New API key" className="font-mono" />
              <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span className="sr-only">Copy key</span>
              </Button>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => setFreshSecret(null)}>
              Done
            </Button>
          </div>
        )}

        {/* Create */}
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || createMutation.isPending || atLimit) return;
            createMutation.mutate();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="api-key-name">Key name</Label>
            <Input
              id="api-key-name"
              value={name}
              maxLength={100}
              placeholder="Claude Code"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {workspaces.length > 0 && (
            <div className="space-y-1">
              <Label htmlFor="api-key-workspace">This key acts on</Label>
              <select
                id="api-key-workspace"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
              >
                <option value={PERSONAL}>My personal data</option>
                {workspaces.map((w) => (
                  <option key={w.holderId} value={w.holderId}>
                    {w.holderDisplayName}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                {workspaceId === PERSONAL
                  ? 'Your own data: email, calendar, todo lists, contacts, documents, chat messages, billing and spending, and your workspace memberships.'
                  : `Shared data in ${workspaceName(workspaceId)} only — todo lists, documents, contacts, CRM records and social drafts held by that workspace. Your role there still applies, so the key never grants more than you have. It CANNOT reach anything personal: not your email, calendar, chat messages, billing, or your other workspaces. Use a separate personal key for those.`}
              </p>
            </div>
          )}

          <div className="flex items-start gap-2">
            <Checkbox
              id="api-key-writes"
              checked={allowWrites}
              onCheckedChange={(checked) => setAllowWrites(checked === true)}
            />
            <Label htmlFor="api-key-writes" className="text-sm font-normal leading-snug">
              Allow this agent to make changes
              <span className="block text-muted-foreground">
                Create and update todo items, drafts, contacts, and similar. Leave unchecked for
                read-only access.
              </span>
            </Label>
          </div>

          {atLimit && (
            <p className="text-sm text-muted-foreground">
              You have reached the limit of {MAX_KEYS} active keys. Revoke one to create another.
            </p>
          )}

          <Button type="submit" disabled={!name.trim() || createMutation.isPending || atLimit}>
            {createMutation.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </form>

        {/* Existing keys */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Your keys</h3>

          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

          {!isLoading && activeKeys.length === 0 && (
            <p className="text-sm text-muted-foreground">No API keys yet.</p>
          )}

          {activeKeys.map((key) => (
            <div
              key={key.keyId}
              className="flex items-start justify-between gap-4 rounded-lg border p-3"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{key.name}</span>
                  <Badge variant="secondary">
                    {key.scopes.includes(WRITE_SCOPE) ? 'Read and write' : 'Read only'}
                  </Badge>
                  {/* A key acting on shared data is the one a reader most
                      needs to spot when auditing this list. */}
                  {boundWorkspaceId(key.scopes) && (
                    <Badge variant="outline">
                      {workspaceName(boundWorkspaceId(key.scopes) as string)}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground font-mono">{key.keyPrefix}…</p>
                <p className="text-xs text-muted-foreground">
                  Created {formatDate(key.createdAt)} · Last used {formatDate(key.lastUsedAt)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={revokeMutation.isPending}
                onClick={() => revokeMutation.mutate(key.keyId)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Revoke {key.name}</span>
              </Button>
            </div>
          ))}
        </div>

        {/* Setup */}
        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-medium">Connect Claude Code</h3>
          <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
            <code>
              {
                'claude mcp add fias \\\n  -e FIAS_API_KEY=your_key_here \\\n  -- npx -y @fias/mcp-server'
              }
            </code>
          </pre>
          <p className="text-xs text-muted-foreground">
            Revoke a key here at any time. Access stops within a few minutes.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
